<template>
<!-- eslint-disable max-len -->
<array-input
  :items="tooltips"
  :sections="sections"
>
  <h4 slot="title"><slot v-if="!noTitle">Tooltips</slot></h4>

  <template slot="item" slot-scope="{ item: tooltip, index: i }">
    <select-input
      v-model="tooltip.target"
      label
      :options="targetTypes"
      :display="targetTypeToDisplayName"
      :remove="() => { remove(i); }"
      :up="() => { up(i); }"
      :down="() => { down(i); }"
    >
      Tooltip Target (optional, default target is the screen)
    </select-input>

    <collapsible-section
      name="Highlight Settings"
      :id="`${id}-${i}-HS`"
    >
      <h6>Highlight Position Offset (relative to Target, or relative to the center of the screen if no Target)</h6>
      <div style="display: flex; flex-direction: row;">
        <text-input
          label
          v-model.trim="tooltip.highlightXOffset"
          placeholder="X Offset"
          no-count
        >
          X Offset (use CSS units) (e.g. 50px)
        </text-input>
        <text-input
          label
          v-model.trim="tooltip.highlightYOffset"
          placeholder="Y Offset"
          no-count
        >
          Y Offset (use CSS units) (e.g. 50px)
        </text-input>
      </div>

      <h6>Highlight Size</h6>
      <div style="display: flex; flex-direction: row;">
        <text-input
          label
          v-model.trim="tooltip.highlightHeight"
          placeholder="100%"
          no-count
        >
          Height (e.g. 50px or 100%)
        </text-input>
        <text-input
          label
          v-model.trim="tooltip.highlightWidth"
          placeholder="100%"
          no-count
        >
          Width (e.g. 50px or 100%)
        </text-input>
        <text-input
          label
          v-model.trim="tooltip.highlightBorderRadius"
          placeholder="(Rounds the corners; 50% for a circle)"
          no-count
        >
          Corner Radius (e.g. 5px or 24px or 50%)
        </text-input>
      </div>
      <a
        href="https://developer.mozilla.org/en-US/docs/Web/CSS/outline"
        target="_blank"
        style="font-size: 70%;"
      >CSS outline documentation</a>
      <text-input
        label
        v-model.trim="tooltip.highlightBorder"
        placeholder="(no outline) (use CSS outline value to specify an outline)"
        no-count
      >
        Highlight Outline (e.g. 4px solid rgba(245, 55, 5, 0.6)) (set Highlight Color alpha to 0: outline only)
      </text-input>

      <div
        v-if="tooltip.highlightColor != null && tooltip.highlightColor.length > 0"
        :style="`height: 25px; width: 50px; background-color: ${tooltip.highlightColor}; border: ${tooltip.highlightBorder};`"
      />
      <text-input label v-model.trim="tooltip.highlightColor" no-count placeholder="rgba(231, 154, 21, 0.25)">
        Highlight Color (recommend using rgba() with low alpha, e.g. rgba(231, 154, 21, 0.25))
      </text-input>
    </collapsible-section>

    <select-input
      v-model="tooltip.textPosition"
      label
      :options="tooltipTextPositionOptions"
      :display="textPositionOptionToDisplayName"
    >
      Text Position (relative to highlight, optional, defaults to top)
    </select-input>

    <text-input
      label
      v-model.trim="tooltip.text"
      textarea
    >
      Tooltip Text
    </text-input>
    <voice-line-input
      :text="tooltip.text"
      :data-object="tooltip"
      field-name="textVoiceLine"
    ></voice-line-input>

    <collapsible-section
      name="Tooltip Text Appearance"
      :id="`${id}-${i}-TS`"
    >
      <div
        v-if="tooltip.textBackgroundColor != null && tooltip.textBackgroundColor.length > 0"
        :style="`height: 25px; width: 50px; background-color: ${tooltip.textBackgroundColor};`"
      />
      <text-input label v-model.trim="tooltip.textBackgroundColor" no-count placeholder="(black)">
        Tooltip Text Background Color (e.g. rgba(17, 17, 17, 0.9))
      </text-input>
      <text-input
        label
        v-model.trim="tooltip.textWidth"
        placeholder="(e.g. 400px; leave blank for default behavior)"
        no-count
      >
        Tooltip Text Width (leave blank for default behavior which varies on text length to a max of 330px)
      </text-input>
    </collapsible-section>

    <collapsible-section
      name="Tooltip Behavior"
      :id="`${id}-${i}-TB`"
    >
      <checkbox-input v-model="tooltip.highlightBlocksClick">
        Highlight Blocks Clicks (so clicking won't press a highlighted button)
      </checkbox-input>
      <checkbox-input v-model="tooltip.requireClickOnHighlight">
        Tooltip Closes Only If You Click On The Highlight (by default, clicking anywhere works)
      </checkbox-input>
      <checkbox-input
        v-if="tooltip.requireClickOnHighlight"
        v-model="tooltip.allowClickOnTooltip"
      >
        Tooltip Can Also Close If You Click On The Tooltip Text Instead Of The Highlight
      </checkbox-input>
      <checkbox-input v-model="tooltip.preventStateProgressionWhileOpen">
        Prevent State Progression While Open (Next and carousel Select buttons go to next tooltip instead)
      </checkbox-input>
      <checkbox-input v-model="tooltip.preventOtherInputWhileOpen">
        Prevent All Other Input While Open (clicks in game can only go to next tooltip and do nothing else)
      </checkbox-input>
    </collapsible-section>

    <hr v-if="i < tooltips.length - 1" style="border: 1px solid #aaa;">
  </template>
  <span slot="button">Add Tooltip</span>
</array-input>
</template>

<script>
import ArrayInput from '../inputs/ArrayInput';
import CheckboxInput from '../inputs/CheckboxInput';
import CollapsibleSection from './CollapsibleSection';
import SelectInput from '../inputs/SelectInput';
import TextInput from '../inputs/TextInput';
import VoiceLineInput from '../inputs/VoiceLineInput';

export default {
  props: {
    tooltips: { type: Array, required: true },
    targetTypes: { type: Array, required: true },
    noTitle: { type: Boolean },
    sections: { type: Array },
    id: { type: String, default: 'Tooltips' },
  },
  components: {
    ArrayInput,
    CheckboxInput,
    CollapsibleSection,
    SelectInput,
    TextInput,
    VoiceLineInput,
  },
  data() {
    return {
      // popover.js position options
      tooltipTextPositionOptions: [
        'top',
        'topleft',
        'topright',
        'right',
        'righttop',
        'rightbottom',
        'bottom',
        'bottomleft',
        'bottomright',
        'left',
        'lefttop',
        'leftbottom',
      ],
    };
  },
  computed: {
  },
  methods: {
    up(index) {
      if (index <= 0) return;
      const [item] = this.tooltips.splice(index, 1);
      this.tooltips.splice(index - 1, 0, item);
    },
    down(index) {
      if (index >= this.tooltips.length - 1) return;
      const [item] = this.tooltips.splice(index, 1);
      this.tooltips.splice(index + 1, 0, item);
    },
    remove(index) {
      this.tooltips.splice(index, 1);
    },
    targetTypeToDisplayName(t) {
      // TODO
      return t;
    },
    textPositionOptionToDisplayName(t) {
      // TODO
      return t;
    },
  },
  created() {
  },
  mounted() {
  },
  watch: {
  }
};
</script>

<style lang="scss" scoped>
.form-drawer {
  margin: 0 200px 10px 1px;
  padding-left: 20px;
  border-left: 1px solid lightgrey;
  border-bottom: 1px solid lightgrey;
}
</style>



// WEBPACK FOOTER //
// src/components/ui/inputs/TooltipInput.vue